import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  EspdResponseDataType,
  type EspdResponseDto,
  type PortalSourcingEventDetailDto,
  type PublicBidDto,
  type PublicBidPatchDto,
  type ResolvedEspdCriterionDto,
} from "../../../../../autogen/bff-api";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { EspdCriterionForm } from "../espd/EspdCriterionForm";
import { isPublicBidEditable } from "../publicBidEditability";
import {
  emptyEspd,
  espdAnswerFor,
  setEspdAnswer,
} from "../espd/useEspdAnswers";

interface Props {
  bidId: string;
  event: PortalSourcingEventDetailDto;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const ExclusionStep = ({
  bidId,
  event,
  previousStep,
  nextStep,
  nextStepLabel,
}: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<
    PublicBidDto,
    PublicBidPatchDto
  >(bidId);
  const readOnly = !isPublicBidEditable(state);
  const espd: EspdResponseDto = state.espdResponse ?? emptyEspd();
  const criteria = event.applicableExclusionCriteria ?? [];

  const saveEspd = (next: EspdResponseDto) =>
    updateField({ espdResponse: next }, { debounceMs: 300 });
  const isSaving =
    fieldStatus("espdResponse") === "saving" ||
    fieldStatus("espdResponse") === "pending";
  const answeredCount = criteria.filter((criterion) =>
    isCriterionAnswered(criterion, espd),
  ).length;
  const allCriteriaAnsweredNo =
    criteria.length > 0 &&
    criteria.every((criterion) => isCriterionAnsweredNo(criterion, espd));

  const declareAllNo = () => {
    let next = espd;
    criteria.forEach((c) => {
      const lead = c.requirementGroups[0]?.requirements.find(
        (r) => r.responseDataType === EspdResponseDataType.Indicator,
      );
      if (lead) {
        next = setEspdAnswer(next, lead.id, { indicator: false });
      }
    });
    saveEspd(next);
  };

  const byCategory = groupByCategory(criteria);

  return (
    <Box pb="8" pt="6">
      <Flex
        align={{ base: "start", md: "center" }}
        justify="space-between"
        gap="3"
        mb="2"
        flexWrap="wrap"
      >
        <HStack spacing="2" align="center" flexWrap="wrap">
          <Heading size="md">{t("Grounds for exclusion")}</Heading>
          <Badge
            bg="smPrimarySofter"
            color="smPrimary"
            px="2"
            py="0.5"
            borderRadius="md"
            textTransform="none"
          >
            {t("ESPD Part III")}
          </Badge>
          {isSaving && <Spinner size="sm" color="smMuted" />}
        </HStack>
        <Text
          fontSize="sm"
          fontWeight="semibold"
          color={answeredCount === criteria.length ? "smSuccess" : "smMuted"}
        >
          {t("{{answered}} of {{total}} answered", {
            answered: answeredCount,
            total: criteria.length,
          })}
        </Text>
      </Flex>
      <Text fontSize="sm" color="smMuted" mb="4">
        {t(
          "Answer whether any of the following grounds for exclusion apply to the supplier.",
        )}
      </Text>
      {!readOnly && (
        <Box
          borderWidth="1px"
          borderColor={
            allCriteriaAnsweredNo ? "smSuccessSoftBorder" : "smPrimarySoft"
          }
          bg={allCriteriaAnsweredNo ? "smSuccessSoft" : "smPrimarySofter"}
          rounded="lg"
          p={allCriteriaAnsweredNo ? "3" : "4"}
          mb="6"
        >
          {allCriteriaAnsweredNo ? (
            <Text fontSize="sm" fontWeight="semibold" color="smSuccess">
              {t("All exclusion grounds answered no")}
            </Text>
          ) : (
            <Stack spacing="3" align="flex-start">
              <Box>
                <Text fontSize="sm" fontWeight="semibold">
                  {t("Not covered by any exclusion grounds")}
                </Text>
                <Text fontSize="sm" color="smMuted">
                  {t(
                    "Use this when no grounds for exclusion apply to the supplier.",
                  )}
                </Text>
              </Box>
              <Button
                size="sm"
                colorScheme="teal"
                onClick={declareAllNo}
                whiteSpace="normal"
                textAlign="left"
              >
                {t("Declare that no grounds apply")}
              </Button>
            </Stack>
          )}
        </Box>
      )}
      <Stack spacing="8">
        {byCategory.map(([category, list]) => (
          <Box key={category}>
            <Flex
              align={{ base: "start", sm: "center" }}
              justify="space-between"
              gap="3"
              mb="3"
              flexWrap="wrap"
            >
              <Box>
                <Heading size="sm">{t(category)}</Heading>
              </Box>
              <Badge
                bg={
                  list.every((criterion) =>
                    isCriterionAnswered(criterion, espd),
                  )
                    ? "smSuccessSoft"
                    : "smBackgroundSecondary"
                }
                color={
                  list.every((criterion) =>
                    isCriterionAnswered(criterion, espd),
                  )
                    ? "smSuccess"
                    : "smMuted"
                }
                px="2"
                py="0.5"
                borderRadius="md"
                textTransform="none"
              >
                {t("{{answered}} of {{total}} answered", {
                  answered: list.filter((criterion) =>
                    isCriterionAnswered(criterion, espd),
                  ).length,
                  total: list.length,
                })}
              </Badge>
            </Flex>
            <Stack spacing="4">
              {list.map((c) => (
                <EspdCriterionForm
                  key={c.id}
                  criterion={c}
                  espd={espd}
                  onChange={saveEspd}
                  readOnly={readOnly}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
      <Box mt="8">
        <StepNavigation
          onPrevious={previousStep}
          onNext={nextStep}
          nextLabel={nextStepLabel}
        />
      </Box>
    </Box>
  );
};

const groupByCategory = (
  criteria: ResolvedEspdCriterionDto[],
): [string, ResolvedEspdCriterionDto[]][] => {
  const map = new Map<string, ResolvedEspdCriterionDto[]>();
  criteria.forEach((c) =>
    map.set(c.category, [...(map.get(c.category) ?? []), c]),
  );
  return [...map.entries()];
};

const leadIndicator = (criterion: ResolvedEspdCriterionDto) =>
  criterion.requirementGroups[0]?.requirements.find(
    (r) => r.responseDataType === EspdResponseDataType.Indicator,
  );

const isCriterionAnswered = (
  criterion: ResolvedEspdCriterionDto,
  espd: EspdResponseDto,
): boolean => {
  const lead = leadIndicator(criterion);
  return lead ? espdAnswerFor(espd, lead.id)?.indicator !== undefined : true;
};

const isCriterionAnsweredNo = (
  criterion: ResolvedEspdCriterionDto,
  espd: EspdResponseDto,
): boolean => {
  const lead = leadIndicator(criterion);
  return lead ? espdAnswerFor(espd, lead.id)?.indicator === false : true;
};
